import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { SECURITY_LEVELS, TIERS } from '../lib/constants'
import { ROLES } from '../lib/roles'
import { T, card, cardTitle, btnPrimary, alert as ALT } from '../lib/theme'

const SEC_COLOUR = {
  Green:  { c:'#3fb950', bg:'rgba(63,185,80,.15)',  border:'rgba(63,185,80,.3)'  },
  Orange: { c:'#d29922', bg:'rgba(210,153,34,.15)', border:'rgba(210,153,34,.3)' },
  Red:    { c:'#f85149', bg:'rgba(248,81,73,.15)',  border:'rgba(248,81,73,.3)'  },
}

const TIER_COLOUR = {
  A: { c:'#58e6a0', bg:'rgba(88,230,160,.12)',  border:'rgba(88,230,160,.25)'  },
  B: { c:'#1E90FF', bg:'rgba(30,144,255,.12)',  border:'rgba(30,144,255,.25)'  },
  C: { c:'#d29922', bg:'rgba(210,153,34,.12)',  border:'rgba(210,153,34,.25)'  },
  D: { c:'#f85149', bg:'rgba(248,81,73,.12)',   border:'rgba(248,81,73,.25)'   },
}

const CLASSES = [
  { key:'1', label:'Class 1' },
  { key:'2', label:'Class 2' },
  { key:'3', label:'Class 3' },
]

const inp = {
  width:'100%', padding:'9px 12px', background:T.surface3,
  border:`1px solid ${T.border}`, borderRadius:6, fontSize:12,
  fontFamily:T.font, color:T.txtPrimary, outline:'none',
}

// Effective target = base * season_factor * security_factor
function effectiveTarget(base, season, securityLevel) {
  const seasonF = season === 'rain' ? 0.75 : 1.0
  const secF    = securityLevel === 'Red' ? 0.50 : securityLevel === 'Orange' ? 0.75 : 1.0
  // Dangerous (Red) always triggers the 25% reduction regardless of season
  // (they stack: Red season rain = 0.50 * 0.75, but spec says Red → always −25% = ×0.75 min)
  // Spec: "reduced by 25% once security level is changed to dangerous regardless of selected season"
  // So Red security = effective target * 0.75 independent of season:
  const secOnly = securityLevel === 'Red' ? 0.75 : 1.0
  return Math.round(base * seasonF * secOnly)
}

export default function Settings({ session, roleCtx }) {
  const role    = roleCtx?.role || 'guest'
  const isAdmin = role === ROLES.ADMIN

  const [tierTargets, setTierTargets] = useState({
    A:{ '1':'', '2':'', '3':'' },
    B:{ '1':'', '2':'', '3':'' },
    C:{ '1':'', '2':'', '3':'' },
    D:{ '1':'', '2':'', '3':'' },
  })
  const [activeTier, setActiveTier] = useState('A')
  const [season,     setSeason]     = useState('dry')
  const [lgas,       setLgas]       = useState([])
  const [loading,    setLoading]    = useState(true)
  const [msg,        setMsg]        = useState(null)
  const [savingT,    setSavingT]    = useState(false)

  async function load() {
    const [{ data:lg }, { data:tg }, { data:se }] = await Promise.all([
      supabase.from('lgas').select('*').order('name'),
      supabase.from('weekly_targets').select('*').order('effective_from', { ascending:false }).limit(40),
      supabase.from('app_settings').select('*'),
    ])
    setLgas(lg || [])

    if (tg && tg.length) {
      const seen = new Set()
      const built = { A:{'1':'','2':'','3':''}, B:{'1':'','2':'','3':''}, C:{'1':'','2':'','3':''}, D:{'1':'','2':'','3':''} }
      tg.forEach(t => {
        const key = `${t.tier}-${t.agent_class}`
        if (!seen.has(key) && built[t.tier]) {
          built[t.tier][t.agent_class] = t.dry_season_target
          seen.add(key)
        }
      })
      setTierTargets(built)
    }

    if (se) { const s = se.find(x => x.key === 'season'); if (s) setSeason(s.value) }
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  function setTarget(tier, cls, val) {
    setTierTargets(prev => ({ ...prev, [tier]: { ...prev[tier], [cls]: val } }))
  }

  async function saveTargets() {
    if (!isAdmin) return
    setSavingT(true); setMsg(null)
    const today = new Date().toISOString().slice(0,10)
    const rows = []
    TIERS.forEach(tier => {
      CLASSES.forEach(({ key }) => {
        const val = Number(tierTargets[tier][key])
        if (val > 0) rows.push({ tier, agent_class:key, dry_season_target:val, effective_from:today })
      })
    })
    if (!rows.length) { setMsg({ type:'err', text:'Enter at least one target amount.' }); setSavingT(false); return }
    const { error } = await supabase.from('weekly_targets').insert(rows)
    setMsg(error
      ? { type:'err', text:error.message }
      : { type:'ok', text:`Targets saved for all tiers (${rows.length} records).` })
    setSavingT(false)
  }

  async function saveSeason(s) {
    if (!isAdmin) return
    setSeason(s)
    await supabase.from('app_settings').upsert([{ key:'season', value:s }], { onConflict:'key' })
    setMsg({ type:'ok', text:`Season set to ${s === 'dry' ? 'Dry Season' : 'Rainy Season (−25%)'}` })
  }

  async function updateLga(id, field, value) {
    if (!isAdmin) return
    // If setting security to Red, show note
    if (field === 'security_level' && value === 'Red') {
      setMsg({ type:'info', text:'Security level set to Dangerous — LGA targets automatically reduced by 25% regardless of season.' })
    }
    setLgas(ls => ls.map(l => l.id === id ? { ...l, [field]: value } : l))
    await supabase.from('lgas').update({ [field]: value }).eq('id', id)
  }

  if (loading) return <div style={{ color:T.txtSecondary, fontSize:13 }}>Loading settings…</div>

  const tc = TIER_COLOUR[activeTier]
  const seasonFactor = season === 'rain' ? 0.75 : 1.0

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:20, fontWeight:800, color:T.txtPrimary, marginBottom:4 }}>Settings</div>
        <div style={{ fontSize:12, color:T.txtSecondary }}>
          Weekly targets by tier · Season · Security levels · LGA tier configuration
        </div>
      </div>

      {!isAdmin && (
        <div style={{ ...ALT.info, marginBottom:16 }}>
          🔒 Settings are view-only for your role. Contact an Admin to make changes.
        </div>
      )}

      {msg && <div style={ALT[msg.type] || ALT.info}>{msg.text}</div>}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>

        {/* ── Tier-based targets ── */}
        <div style={card}>
          <div style={cardTitle}>
            Weekly Collection Targets by Tier (₦)
            {!isAdmin && <span style={{ fontSize:10, color:T.txtMuted, fontWeight:400, marginLeft:8 }}>· View only</span>}
          </div>
          <div style={{ fontSize:11, color:T.txtSecondary, marginBottom:14, lineHeight:1.6 }}>
            Dry season = full target. Rainy season = <strong style={{ color:T.txtPrimary }}>−25%</strong>.
            Dangerous security level = additional <strong style={{ color:'#f85149' }}>−25%</strong> (stacks).
          </div>

          {/* Tier tabs */}
          <div style={{ display:'flex', gap:8, marginBottom:18 }}>
            {TIERS.map(tier => {
              const tc2 = TIER_COLOUR[tier]
              const isActive = activeTier === tier
              return (
                <button key={tier} onClick={() => setActiveTier(tier)} style={{
                  flex:1, padding:'10px 6px', borderRadius:8, cursor:'pointer',
                  fontFamily:T.font, fontWeight:700, fontSize:13, transition:'all .15s',
                  background: isActive ? tc2.bg : T.surface3,
                  border: isActive ? `1.5px solid ${tc2.border}` : `1.5px solid ${T.border}`,
                  color: isActive ? tc2.c : T.txtMuted,
                }}>
                  Tier {tier}
                  {tierTargets[tier]['1'] ? (
                    <div style={{ fontSize:9, fontWeight:400, color:isActive ? tc2.c : T.txtMuted, marginTop:3 }}>
                      ₦{Number(tierTargets[tier]['1']).toLocaleString()}
                    </div>
                  ) : (
                    <div style={{ fontSize:9, color:T.txtMuted, marginTop:3 }}>not set</div>
                  )}
                </button>
              )
            })}
          </div>

          {/* Class inputs */}
          <div style={{ background:T.surface3, border:`1px solid ${tc.border}`, borderRadius:10,
            padding:'16px', marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:tc.c, marginBottom:14,
              textTransform:'uppercase', letterSpacing:'.06em' }}>
              Tier {activeTier} — {season === 'dry' ? 'Dry Season' : 'Rainy Season (×0.75)'}
            </div>
            {CLASSES.map(({ key, label:lbl }) => {
              const base = Number(tierTargets[activeTier][key]) || 0
              const eff  = base > 0 ? Math.round(base * seasonFactor) : 0
              return (
                <div key={key} style={{ marginBottom:14 }}>
                  <label style={{ fontSize:10, fontWeight:600, color:T.txtMuted, display:'block',
                    letterSpacing:'.06em', textTransform:'uppercase', marginBottom:4 }}>
                    {lbl}
                  </label>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ fontSize:13, color:T.txtSecondary }}>₦</span>
                    <input
                      style={{ ...inp, marginBottom:0, opacity: isAdmin ? 1 : 0.6,
                        cursor: isAdmin ? 'text' : 'not-allowed' }}
                      type="number"
                      value={tierTargets[activeTier][key]}
                      onChange={e => isAdmin && setTarget(activeTier, key, e.target.value)}
                      readOnly={!isAdmin}
                      placeholder="e.g. 120000"
                    />
                    {base > 0 && season === 'rain' && (
                      <span style={{ fontSize:10, color:T.blue, whiteSpace:'nowrap' }}>
                        → ₦{eff.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Summary reference table — shows effective amounts per season */}
          <div style={{ border:`1px solid ${T.border}`, borderRadius:8, overflow:'hidden', marginBottom:16 }}>
            <div style={{ background:T.surface2, padding:'6px 12px',
              fontSize:10, fontWeight:700, color:T.txtSecondary, textTransform:'uppercase',
              letterSpacing:'.06em', borderBottom:`1px solid ${T.border}` }}>
              Tier Reference — {season === 'dry' ? 'Dry Season (Full)' : 'Rainy Season (×0.75)'}
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
              <thead><tr>
                <th style={{ background:T.surface2, color:T.txtSecondary, padding:'7px 12px',
                  textAlign:'left', fontSize:10, textTransform:'uppercase', letterSpacing:'.06em',
                  borderBottom:`1px solid ${T.border}` }}>Tier</th>
                {CLASSES.map(c => (
                  <th key={c.key} style={{ background:T.surface2, color:T.txtSecondary, padding:'7px 12px',
                    textAlign:'right', fontSize:10, textTransform:'uppercase', letterSpacing:'.06em',
                    borderBottom:`1px solid ${T.border}` }}>{c.label}</th>
                ))}
              </tr></thead>
              <tbody>
                {TIERS.map(tier => {
                  const tc2 = TIER_COLOUR[tier]
                  return (
                    <tr key={tier} style={{ background: tier === activeTier ? tc2.bg : 'transparent',
                      cursor:'pointer' }} onClick={() => setActiveTier(tier)}>
                      <td style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}`,
                        fontWeight:700, color:tc2.c }}>Tier {tier}</td>
                      {CLASSES.map(c => {
                        const base = Number(tierTargets[tier][c.key]) || 0
                        const eff  = base > 0 ? Math.round(base * seasonFactor) : 0
                        return (
                          <td key={c.key} style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}`,
                            textAlign:'right', color: base ? T.txtPrimary : T.txtMuted }}>
                            {base
                              ? <>
                                  ₦{eff.toLocaleString()}
                                  {season === 'rain' && (
                                    <div style={{ fontSize:9, color:T.txtMuted }}>
                                      base ₦{base.toLocaleString()}
                                    </div>
                                  )}
                                </>
                              : '—'}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {isAdmin && (
            <button style={{ ...btnPrimary, width:'100%' }} onClick={saveTargets} disabled={savingT}>
              {savingT ? 'Saving…' : 'Save All Tier Targets'}
            </button>
          )}
        </div>

        {/* ── Season ── */}
        <div style={card}>
          <div style={cardTitle}>
            Active Season
            {!isAdmin && <span style={{ fontSize:10, color:T.txtMuted, fontWeight:400, marginLeft:8 }}>· View only</span>}
          </div>
          <p style={{ fontSize:11, color:T.txtSecondary, marginBottom:16, lineHeight:1.7 }}>
            Rainy season applies a <strong style={{ color:T.txtPrimary }}>25% reduction</strong> to all agent targets automatically.
            Dangerous security level adds another <strong style={{ color:'#f85149' }}>25% reduction</strong>.
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
            {[
              { v:'dry',  icon:'☀',  label:'Dry Season',   sub:'Oct – Apr · Full targets', c:T.amber },
              { v:'rain', icon:'🌧', label:'Rainy Season', sub:'May – Sep · −25%',          c:T.blue  },
            ].map(({ v, icon, label:lbl, sub, c }) => (
              <button key={v}
                onClick={() => isAdmin && saveSeason(v)}
                disabled={!isAdmin}
                style={{
                  padding:'16px 14px', borderRadius:10,
                  cursor: isAdmin ? 'pointer' : 'default',
                  textAlign:'left', fontFamily:T.font, transition:'all .15s',
                  background: season === v ? `rgba(${c === T.amber ? '245,166,35' : '30,144,255'},.1)` : T.surface3,
                  border: season === v ? `1.5px solid ${c}` : `1.5px solid ${T.border}`,
                  opacity: isAdmin ? 1 : 0.8,
                }}>
                <div style={{ fontSize:18, marginBottom:6 }}>{icon}</div>
                <div style={{ fontSize:12, fontWeight:700, color:season === v ? c : T.txtPrimary, marginBottom:2 }}>{lbl}</div>
                <div style={{ fontSize:10, color:T.txtMuted }}>{sub}</div>
                {season === v && <div style={{ fontSize:9, color:c, marginTop:4, fontWeight:700 }}>● ACTIVE</div>}
              </button>
            ))}
          </div>

          {/* Tier legend */}
          <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:T.txtPrimary, marginBottom:10 }}>
              Tier Reference
            </div>
            {TIERS.map(tier => {
              const tc2 = TIER_COLOUR[tier]
              const lgaCount = lgas.filter(l => l.tier === tier).length
              const base = Number(tierTargets[tier]['1']) || 0
              const eff  = base > 0 ? Math.round(base * seasonFactor) : 0
              return (
                <div key={tier} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                  <span style={{ display:'inline-block', width:28, textAlign:'center',
                    padding:'2px 0', borderRadius:6, fontSize:11, fontWeight:800,
                    background:tc2.bg, color:tc2.c, border:`1px solid ${tc2.border}` }}>
                    {tier}
                  </span>
                  <span style={{ fontSize:11, color:T.txtSecondary }}>
                    {lgaCount} LGA{lgaCount !== 1 ? 's' : ''} · {base
                      ? `₦${eff.toLocaleString()} / agent (Class 1)${season === 'rain' ? ' — rainy' : ''}`
                      : '— not set'}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* ── LGA tier & security table ── */}
      <div style={card}>
        <div style={cardTitle}>
          LGA Tier &amp; Security Level Configuration
          {!isAdmin && <span style={{ fontSize:10, color:T.txtMuted, fontWeight:400, marginLeft:8 }}>· View only — Admin only</span>}
        </div>
        <p style={{ fontSize:11, color:T.txtSecondary, marginBottom:14, lineHeight:1.6 }}>
          <strong style={{ color:T.txtPrimary }}>Tier</strong> sets the weekly target band.&nbsp;
          <strong style={{ color:T.txtPrimary }}>Security</strong>: Green = full · Orange = −25% · Red (Dangerous) = −25% regardless of season.
        </p>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead><tr>
              {['LGA','Zone','Tier','Target (Cl.1)','Effective Target','Security Level'].map(h => (
                <th key={h} style={{ background:T.surface2, color:T.txtSecondary, fontWeight:600,
                  padding:'9px 12px', textAlign:'left', fontSize:10, textTransform:'uppercase',
                  letterSpacing:'.06em', borderBottom:`1px solid ${T.border}`, whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {lgas.map(l => {
                const sc   = SEC_COLOUR[l.security_level] || SEC_COLOUR.Green
                const tc2  = TIER_COLOUR[l.tier] || TIER_COLOUR.C
                const base = Number(tierTargets[l.tier]?.['1']) || 0
                const eff  = base > 0 ? effectiveTarget(base, season, l.security_level) : 0
                const isDangerous = l.security_level === 'Red'
                return (
                  <tr key={l.id}>
                    <td style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}`,
                      fontWeight:600, color:T.txtPrimary }}>{l.name}</td>
                    <td style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}`,
                      color:T.txtSecondary }}>{l.zone}</td>
                    <td style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}` }}>
                      {isAdmin ? (
                        <select value={l.tier} onChange={e => updateLga(l.id, 'tier', e.target.value)}
                          style={{ fontSize:11, background:tc2.bg, border:`1.5px solid ${tc2.border}`,
                            borderRadius:5, padding:'4px 8px', fontFamily:T.font,
                            color:tc2.c, cursor:'pointer', fontWeight:700 }}>
                          {TIERS.map(t => <option key={t}>{t}</option>)}
                        </select>
                      ) : (
                        <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:5,
                          fontSize:11, fontWeight:700,
                          background:tc2.bg, border:`1.5px solid ${tc2.border}`, color:tc2.c }}>
                          {l.tier}
                        </span>
                      )}
                    </td>
                    <td style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}`,
                      color: base ? T.txtPrimary : T.txtMuted, fontSize:11 }}>
                      {base ? `₦${Math.round(base * seasonFactor).toLocaleString()}` : '— not set'}
                    </td>
                    <td style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}`, fontSize:11 }}>
                      {eff
                        ? <span style={{ color: isDangerous ? '#f85149' : '#3fb950', fontWeight:700 }}>
                            ₦{eff.toLocaleString()}
                            {isDangerous && <span style={{ fontSize:9, display:'block', color:'#f85149' }}>⚠ −25% danger</span>}
                          </span>
                        : '—'}
                    </td>
                    <td style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}` }}>
                      {isAdmin ? (
                        <select value={l.security_level}
                          onChange={e => updateLga(l.id, 'security_level', e.target.value)}
                          style={{ fontSize:11, background:sc.bg, border:`1.5px solid ${sc.border}`,
                            borderRadius:5, padding:'4px 8px', fontFamily:T.font,
                            color:sc.c, cursor:'pointer', fontWeight:700 }}>
                          {SECURITY_LEVELS.map(sl => <option key={sl.value} value={sl.value}>{sl.label}</option>)}
                        </select>
                      ) : (
                        <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:5,
                          fontSize:11, fontWeight:700,
                          background:sc.bg, border:`1.5px solid ${sc.border}`, color:sc.c }}>
                          {l.security_level}
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
